import { sdkRequest } from '@eventbrite/http';
import { captureException, captureMessage } from '@sentry/browser';
import { useQuery } from '@tanstack/react-query';
import { formatUrl } from 'url-lib';
import { isRetryableError } from '../api/utils/isRetrayable';
import { withTimeout } from '../api/utils/withTimeout';
import { CountryDTO, CountryValues } from './types';

type CountriesResponse = {
    countries: CountryDTO[];
};

export const MAX_RETRIES = 2;

export const useCountries = (
    timeoutMs = 5000,
    blacklistedCountries: Set<string> = new Set(),
    existingCountryCode?: string,
) => {
    return useQuery({
        queryKey: [
            'countries',
            Array.from(blacklistedCountries).sort().join(','),
            existingCountryCode || '',
        ],
        queryFn: () => withTimeout(fetchCountries, timeoutMs),
        select: (countries) => {
            const adapted = countries.map(countryValueAdapter);
            // Filter out blacklisted countries, but always include existing country
            // This allows editing existing venues in blacklisted countries
            if (blacklistedCountries.size === 0) {
                return adapted;
            }
            return adapted.filter(
                (country) =>
                    !blacklistedCountries.has(country.code) ||
                    country.code === existingCountryCode,
            );
        },
        staleTime: 1000 * 60 * 60 * 24, // countries are stable
        cacheTime: 1000 * 60 * 60 * 24,
        retry: (failureCount, error: any) => {
            if (!isRetryableError(error)) {
                captureMessage('useCountries: non-retryable error', {
                    level: 'info',
                    extra: { error },
                });
                return false;
            }

            const allow = failureCount < MAX_RETRIES;
            if (!allow) {
                captureMessage('useCountries: max retries reached', {
                    level: 'info',
                    extra: { failureCount, error },
                });
            }
            return allow;
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000), // exp backoff up to 10 seconds
        onError: (error: any) => {
            captureException(`Error in GET /api/v3/system/countries/`, error);
        },
    });
};

const fetchCountries = (): Promise<CountryDTO[]> =>
    sdkRequest(
        formatUrl('/api/v3/system/countries/', {
            expand: 'mobile',
        }),
    ).then(({ countries }: CountriesResponse) => countries);

const countryValueAdapter = (country: CountryDTO): CountryValues => ({
    code: country.code,
    label: country.label,
    zipRequired: country.mobile.zip_required,
});
